import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { PHASE_DEVELOPMENT_SERVER, PHASE_PRODUCTION_BUILD } from 'next/constants';
import { useTranslation } from 'next-i18next';

import { CHANNEL } from '@boss/constants/b2b-b2c';
import { events as eventService, store as storeService } from '@boss/services';
import { Crumble, Environment } from '@boss/types/b2b-b2c';
import { getLocalizedSlugForEventPage, getTranslatedSlugsByType, slugify } from '@boss/utils';

import { ComponentMapper, SEO } from '../../components';
import { PageTypes } from '../../constants';
import { WorkshopPage } from '../../containers';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapContentfulToNextLocale, mapNextToContentfulLocale } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { eventType } from '../../utils/options';
const IS_BUILD =
  (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD && !process.env.SKIP_PRODUCT_BUILD) ||
  (process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER && process.env.DEBUG_BUILD === 'true');

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (!locales) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const paths = [];

  if (CHANNEL && locales) {
    for (const locale of locales.filter((locale: string) => locale !== 'default')) {
      const events =
        eventType === 'TRAINING'
          ? await eventService.getTrainings(locale as string, IS_BUILD)
          : await eventService.getWorkshops(locale as string, IS_BUILD);

      for (const event of events) {
        paths.push({ locale: mapContentfulToNextLocale(locale), params: { slug: slugify(event.id) } });
      }
    }
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ preview, locale, params }: GetStaticPropsContext) => {
  const slug = params?.slug;

  if (!slug || Array.isArray(slug) || !locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocale = mapNextToContentfulLocale(locale);

  const [navigation, event] = await Promise.all([
    getMappedNavigation(contentfulLocale, !!preview),
    eventType === 'TRAINING'
      ? eventService.getTrainingById({ locale: contentfulLocale, eventId: slug, batchMode: IS_BUILD })
      : eventService.getWorkshopById({ locale: contentfulLocale, eventId: slug, batchMode: IS_BUILD }),
  ]);

  if (!event) {
    return {
      notFound: true,
    };
  }

  const store = event?.location
    ? (await storeService.getStoreByStoreName(slugify(event?.location), locale)) ??
      (await storeService.getById(event?.locationId, locale))
    : null;

  return {
    props: {
      pageType: PageTypes.EVENT_DETAIL,
      store: store ?? null,
      slug,
      locale,
      preview: !!preview,
      navigation,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      event,
      ...(await getServerSideTranslations(locale, ['common', 'event', 'workshop', 'forms', 'routes'])),
    },
  };
};

export const Index = ({
  slug,
  navigation,
  locale,
  preview,
  environment,
  event,
  store,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const pageDescription = '';
  const pageTitle = event.title;
  const localizedSlugs = getTranslatedSlugsByType(slug, 'Event overview');
  const { t } = useTranslation('event');
  const crumbles: Crumble[] = [
    { href: getLocalizedSlugForEventPage(locale), label: t('pageTitle', { ns: 'event' }) },
    { href: getLocalizedSlugForEventPage(locale, slug), label: pageTitle },
  ];

  return (
    // TODO: add ogImage implementation
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      title={pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        {event?.prefixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
        <WorkshopPage event={event} store={store} />
        {event?.suffixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
      </Layout>
    </SEO>
  );
};

export default Index;
